/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo, useState } from "react";
import RulesSection, { ServiceNotificationStatus } from "../ServicesRules/RulesSection";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericButton from "@ui-components/Button/GenericButton";
import { disableOrganizationalConfig, enableOrganizationalConfig, fetchCustomersServices, fetchMySubscriptions, fetchTemplatesList } from "../requests/request";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { useProcessingOverlay } from "@shared/hooks/useProcessingOverlay";
import { useAppSelector } from "@redux/hooks";
import { RawCustomer, ServiceItem } from "../hooks/useCustomers";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { MyNotificationsInitialSkeleton } from "../NotificationsFetchSkeleton";

interface MyNotificationsConfigProps {
  onConfigChange: (enabled: boolean) => void;
  isOrgConfigEnabled: boolean;
}

export function MyNotificationsConfig({
  isOrgConfigEnabled,
  onConfigChange,
}: MyNotificationsConfigProps) {
  const [portNotificationStates, setPortNotificationStates] = useState<ServiceNotificationStatus[]>([]);
  const [internetNotificationStates, setInternetNotificationStates] = useState<ServiceNotificationStatus[]>([]);
  const [showEnableModal, setShowEnableModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [portTemplates, setPortTemplates] = useState<any[]>([]);
  const [internetTemplates, setInternetTemplates] = useState<any[]>([]);
  const tenantType = useAppSelector((s) => s.userProfile.TenantType);
  const tenantId = useAppSelector(s => s.selection.tenantId);
  const [allServices, setAllServices] = useState<ServiceItem[]>([]);
  const [orgLoading, setOrgLoading] = useState(true);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [isSavingOrg, setIsSavingOrg] = useState(false);
  const [error,       setError]       = useState<string|null>(null);
  const [fetchAsTenant,     setFetchAsTenant]       = useState(false);

  const api = useAxiosAuth0();
  const overlaySpinner = useProcessingOverlay(isSavingOrg);
  const userId = useAppSelector(s => s.userProfile.id);

  const isInitialLoading =
    orgLoading || servicesLoading || templatesLoading;

  useEffect(() => {
    (async () => {
      try {
        const resp = await fetchMySubscriptions(api);
        const userSubs = resp.data.data.userSubscriptions;
        const imported = userSubs.some((s: { organizational: any; }) => s.organizational);
        onConfigChange(imported);
      } catch {
        /**/
      } finally {
        setOrgLoading(false);
      }
    })();
  }, [api, onConfigChange]);

  useEffect(() => {
    if (!tenantId) {
      setAllServices([]);
      setServicesLoading(false);
      return;
    }
    setServicesLoading(true);
    setError(null);

    fetchCustomersServices(api, tenantId)
      .then(resp => {
        const raw = resp.data.data as RawCustomer[];
        const flat: ServiceItem[] = [];
        raw.forEach(cust => {
          [ ...cust.services.ports,
            ...cust.services.internet,
            ...cust.services.cloudConnectivity
          ].forEach(svc => {
            flat.push({
              id: svc.id,
              name: svc.name,
              subscriptionId: svc.subscriptionId,
              type: svc.type,
              customer: {
                id: cust.id,
                name: cust.nameDisplay ?? cust.name,
              }
            });
          });
        });
        setAllServices(flat);
      })
      .catch(() => {
        setError("Unable to load your services. Please try again.");
      })
      .finally(() => {
        setServicesLoading(false);
      });
  }, [api, tenantId]);

  const portServices     = useMemo(
    () => allServices.filter(s => s.type === "port"),
    [allServices]
  );
  const internetServices = useMemo(
    () => allServices.filter(s => s.type === "internet"),
    [allServices]
  );

  useEffect(() => {
    const fetchData = async () => {
      setTemplatesLoading(true);
      try {
        const response = await fetchTemplatesList(api);
        const templates = response.data?.data?.templates || [];

        const enabledTemplates = templates.filter((t: { enabled: any; }) => t.enabled);
        setPortTemplates(
          enabledTemplates.filter((t: { compatibleServices: string | string[]; }) => t.compatibleServices.includes("port"))
        );
        setInternetTemplates(
          enabledTemplates.filter((t: { compatibleServices: string | string[]; }) => t.compatibleServices.includes("internet"))
        );
      } catch {
        /**/
      } finally {
        setTemplatesLoading(false);
      }
    };

    fetchData();
  }, [api]);

  const handleTagClick = () => {
      if (isOrgConfigEnabled) {
        setShowDisableModal(true);
      } else {
        setShowEnableModal(true);
      }
    };

  const confirmEnable = async () => {
    setIsSavingOrg(true);
    try {
      await enableOrganizationalConfig(api);
      onConfigChange(true);
      setShowEnableModal(false);
      setFetchAsTenant(true);
    } catch {
      /**/
    } finally {
      setIsSavingOrg(false);
    }
  };


  const cancelEnable = () => {
      setShowEnableModal(false);
    };

  const confirmDisable = async () => {
    setIsSavingOrg(true);
    try {
      await disableOrganizationalConfig(api);

      onConfigChange(false);
      setFetchAsTenant(false);

    } catch {
      /**/
    } finally {
      setIsSavingOrg(false);
      setShowDisableModal(false);
    }
  };

  const cancelDisable = () => {
      setShowDisableModal(false);
    };

  return (
    <>
      {overlaySpinner}
      {isInitialLoading ? (
        <MyNotificationsInitialSkeleton />
      ) : (
        <>
      <GenericCard
        headerContent="Organizational Configuration"
        isCollapsible={false}
        rightContent={
          <GenericTag
            label={isOrgConfigEnabled ? "Enabled" : "Disabled"}
            variant={isOrgConfigEnabled ? "success" : "error"}
            onClick={handleTagClick}
            className={isOrgConfigEnabled ? "cursor-pointer" : "cursor-pointer"}
            size="small"
          />
        }
        bodyContent={
          <>
            {isOrgConfigEnabled ? (
              <div className="space-y-2">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-700">
                    <strong>Note:</strong> While organizational configuration is
                    enabled, all notification settings in Port Rules and
                    Internet Rules are read-only and cannot be modified.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-semantic-text-secondary mb-2">
                  Organizational configuration is currently
                  <strong> disabled</strong>. You may import your organization’s
                  default notification settings to override custom
                  configurations.
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm text-amber-700">
                    <strong>Click “Disabled”</strong> above to import
                    organizational configuration. This will override any
                    existing custom settings.
                  </p>
                </div>
              </div>
            )}
          </>
        }
      />
      <RulesSection
        title="Port Rules"
        type="port"
        services={portServices}
        customers={[]}
        templates={portTemplates}
        notificationStates={portNotificationStates}
        initialExpanded={true}
        tenantType={tenantType}
        isReadOnly={isOrgConfigEnabled}
        subscriberType={isOrgConfigEnabled ? "tenant" : "user"}
        subscriberId={isOrgConfigEnabled ? String(tenantId) : userId}
        sendTenantId={false}
        showManageRecipients={false}

      />
      <RulesSection
        title="Internet Rules"
        type="internet"
        services={internetServices}
        customers={[]}
        templates={internetTemplates}
        notificationStates={internetNotificationStates}
        initialExpanded={true}
        tenantType={tenantType}
        isReadOnly={isOrgConfigEnabled}
        subscriberType={isOrgConfigEnabled ? "tenant" : "user"}
        subscriberId={isOrgConfigEnabled ? String(tenantId) : userId}
        sendTenantId={false}
        showManageRecipients={false}

      />
        </>
      )}
      {/* Enable Confirmation Modal */}
      <GenericModal
        isOpen={showEnableModal}
        onClose={cancelEnable}
        title={
          <span className="text-lg font-semibold text-semantic-text-primary">
            Enable Organizational Configuration
          </span>
        }
      >
        <p className="mb-4 text-semantic-text-secondary">
          You are about to import the organization&apos;s default notification
          configuration. This action will:
        </p>
        <ul className="list-disc list-inside space-y-1 text-sm text-semantic-text-secondary p-5 bg-[var(--color-primitive-gray-50)] border rounded-lg">
          <li>Load all organizational notification settings</li>
          <li>Override any existing custom configurations</li>
          <li>Make all notification rules read-only</li>
          <li>Apply organization-wide recipient lists</li>
        </ul>
        <p className="mt-4 ml-5 font-medium text-sm text-semantic-text-primary">
          Do you want to continue?
        </p>
        <div className="mt-6 flex justify-end space-x-2">
          <GenericButton
            size="sm"
            variant="secondary"
            onClick={cancelEnable}
            label="Cancel"
          />
          <GenericButton
            size="sm"
            onClick={confirmEnable}
            label="Import Configuration"
          />
        </div>
      </GenericModal>

      {/* Disable Confirmation Modal */}
      <GenericModal
        isOpen={showDisableModal}
        onClose={cancelDisable}
        title={
          <span className="text-lg font-semibold text-semantic-text-primary">
            Disable Organizational Configuration
          </span>
        }
      >
        <p className="text-sm mb-8">
        Disabling organizational configuration will remove all imported 
        organizational alerts and reset your custom notification settings. 
       <span className="text-sm font-medium"> This action will clear every alert subscription. </span>Are you sure you want to continue?
        </p>
        <div className="mt-4 flex justify-end space-x-2">
          <GenericButton
            size="sm"
            variant="secondary"
            onClick={cancelDisable}
            label="Cancel"
          />
          <GenericButton
            size="sm"
            variant="brand"
            onClick={confirmDisable}
            label="Disable"
          />
        </div>
      </GenericModal>
    </>
  );
}
